import { createContext, MutableRefObject, PropsWithChildren, ReactNode, useContext } from 'react';

import { IProposal } from 'utils/itineraryDetails/types';
import { createReactiveVar, TReactiveVar } from 'utils/reactivity';

import { ILandingConfig, TPageHandlersProviderFn, TBlockHandlersProviderFn, ELandingDeviceType } from './types';

export interface ILandingV2Context {
  config: ILandingConfig;
  data: IProposal;
  readonly: boolean;
  rightActions: ReactNode;
  leftActions: ReactNode;
  deviceType?: ELandingDeviceType;
  setActivePageId: (args: TSetActivePageIdArgs) => void;
  isManualScrollRef: MutableRefObject<boolean>;
  contentRef: MutableRefObject<HTMLDivElement | null>;
  headerRef: MutableRefObject<HTMLDivElement | null>;
  landingRef: MutableRefObject<HTMLDivElement | null>;
  pageHandlersProvider: TPageHandlersProviderFn;
  blockHandlersProvider: TBlockHandlersProviderFn;
  reactActivePageId: TReactiveVar<number>;
}

const LandingV2Context = createContext<ILandingV2Context>({
  data: {} as IProposal,
  readonly: true,
  config: {} as ILandingConfig,
  rightActions: null,
  leftActions: null,
  isManualScrollRef: { current: false },
  deviceType: ELandingDeviceType.Desktop,
  contentRef: { current: null },
  headerRef: { current: null },
  landingRef: { current: null },
  setActivePageId: () => {},
  pageHandlersProvider: () => <div />,
  blockHandlersProvider: () => <div />,
  reactActivePageId: createReactiveVar(0),
});
export const useLandingV2Context = () => useContext(LandingV2Context);

interface ILandingV2ContextProviderProps extends PropsWithChildren {
  value: ILandingV2Context;
}

export const LandingV2ContextProvider = ({ value, children }: ILandingV2ContextProviderProps) => {
  return <LandingV2Context.Provider value={value}>{children}</LandingV2Context.Provider>;
};

export type TSetActivePageIdArgs = {
  pageId: number;
  notifyExternalEnv?: boolean;
  withScroll?: boolean;
};
